/**
 * PURPOSE: Vista Por Sede / Tabla de Programación del Centro (FTR-0028 WG3, §19 / vista 4).
 * RESPONSIBILITIES: Reproducir la jerarquía que los operadores ya conocen — SEDE → FECHA → partidos
 *   — con edición de hora y cancha en línea, acciones masivas por sede y una salida apta para
 *   imprimir desde el navegador.
 *
 *   ES LA VISTA FAMILIAR. La Programación actual de Winscore se lee así, y el Centro no debería
 *   obligar a reaprender la supervisión de un torneo. Mismas columnas, mismo orden (fecha → hora →
 *   `nro_match`) y la misma bandeja final "Sin sede asignada".
 *
 *   IMPRIMIBLE, TODAVÍA NO PDF. El PDF por sede es WG6 (D-40). Aquí se deja la estructura lista:
 *   `print:` oculta selección y controles, y cada sede empieza en página nueva. Un operador puede
 *   imprimir hoy sin esperar al generador.
 *
 *   SIN BYE (D-20): un descanso no ocupa cancha ni aparece en la programación impresa.
 * COLLABORATORS: ProgrammingCenterProvider.jsx, MatchRow.jsx, utils/programmingCenterGrouping.js.
 * ROUTE: winscore/src/area/FasesArea/ProgrammingCenter/views/VenueView.jsx
 */
import React, { useMemo, useState } from "react";
import { FiDownload, FiMapPin, FiPrinter } from "react-icons/fi";
import { toast } from "react-hot-toast";

import { downloadVenueSchedulePdf } from "../../../../services/programmingCenterService.js";
import { useProgrammingCenter } from "../ProgrammingCenterProvider.jsx";
import { conflictSeverity, groupByVenue } from "../../../../utils/programmingCenterGrouping.js";
import MatchRow from "../components/MatchRow.jsx";

const COLUMNS = ["", "N°", "Fecha", "Partido", "Hora", "Cancha", "Grupo / Etapa", "Estado"];

const VenueView = () => {
  const {
    visibleRows,
    context,
    selection,
    toggleSelection,
    setBlockSelection,
    conflictsByKey,
    scheduleDraft,
    setScheduleDraft,
    courtsByVenue,
    busy,
    actions,
    scope,
  } = useProgrammingCenter();

  const venues = useMemo(() => groupByVenue(visibleRows, context), [visibleRows, context]);

  const onScheduleChange = (row, field, value) =>
    setScheduleDraft((current) => actions.changeScheduleField(current, row, field, value));

  const [downloading, setDownloading] = useState(false);

  /**
   * Descarga el PDF sin sacar al operador de la pantalla. El enlace temporal es la forma estándar de
   * entregar un blob; se revoca siempre, también si el navegador cancela la descarga.
   */
  const downloadPdf = async (source) => {
    if (downloading) return;
    setDownloading(true);
    let objectUrl = null;
    try {
      const { blob, filename } = await downloadVenueSchedulePdf({ ...scope, source });
      objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error("No se pudo generar el PDF por sede. Intenta de nuevo.");
      console.error("[programming-center] PDF por sede:", error);
    } finally {
      if (objectUrl) window.URL.revokeObjectURL(objectUrl);
      setDownloading(false);
    }
  };

  if (!venues.length) {
    return (
      <p className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm font-bold text-slate-500">
        No hay partidos programables que coincidan con los filtros actuales.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap justify-end gap-2 print:hidden">
        {/* FTR-0028 WG6 (D-40): el PDF por sede lo genera el servidor con la MISMA fuente
            operacional que esta tabla, así que lo impreso y lo que se ve en pantalla no pueden
            divergir. La impresión del navegador se mantiene: sirve cuando hace falta una copia
            inmediata y no depende de Puppeteer. */}
        <button
          type="button"
          onClick={() => downloadPdf("matches")}
          disabled={downloading}
          className="flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-sm font-black text-blue-700 transition hover:border-blue-400 disabled:cursor-not-allowed disabled:opacity-50"
          title="PDF por sede y fecha de los partidos ya materializados"
        >
          <FiDownload className="text-sm" />
          {downloading ? "Generando…" : "PDF por sede"}
        </button>
        <button
          type="button"
          onClick={() => downloadPdf("draft")}
          disabled={downloading}
          className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-black text-slate-600 transition hover:border-blue-300 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          title="Incluye las filas que todavía son borrador; el PDF sale marcado BORRADOR"
        >
          <FiDownload className="text-sm" />
          PDF borrador
        </button>
        <button
          type="button"
          onClick={() => window.print()}
          className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-black text-slate-600 transition hover:border-blue-300 hover:text-blue-700"
        >
          <FiPrinter className="text-sm" />
          Imprimir programación
        </button>
      </div>

      {venues.map((venue) => {
        const selectable = venue.rows.filter((row) => row.canSelect);
        const allSelected =
          selectable.length > 0 && selectable.every((row) => selection.has(row.iddraftmatch));

        return (
          <section
            key={venue.idsede || "unassigned"}
            className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm print:break-before-page print:shadow-none"
          >
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 bg-slate-50/80 px-3 py-2">
              <div className="flex items-center gap-3">
                <span className="print:hidden">
                  {selectable.length ? (
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={(event) => setBlockSelection(selectable, event.target.checked)}
                      aria-label={`Seleccionar los partidos de ${venue.name}`}
                      className="h-4 w-4 cursor-pointer rounded border-slate-300 text-blue-600 focus:ring-blue-400"
                    />
                  ) : null}
                </span>
                <div>
                  <p className="flex items-center gap-2 text-sm font-black text-slate-900">
                    <FiMapPin className="text-xs text-slate-400" />
                    {venue.name}
                  </p>
                  {venue.address ? (
                    <p className="text-[11px] font-bold text-slate-500">{venue.address}</p>
                  ) : null}
                </div>
              </div>
              <span className="text-[11px] font-black uppercase tracking-wide text-slate-500">
                {venue.rows.length} {venue.rows.length === 1 ? "partido" : "partidos"}
              </span>
            </div>

            {venue.dates.map((bucket) => (
              <div key={`${venue.idsede || "none"}-${bucket.date || "none"}`}>
                <div className="bg-blue-50/50 px-3 py-1.5">
                  <span className="text-[11px] font-black uppercase tracking-wide text-blue-700">
                    {bucket.date || "Sin fecha asignada"}
                  </span>
                </div>
                <table className="w-full table-auto border-collapse text-left">
                  <thead>
                    <tr className="border-b border-slate-200 bg-white">
                      {COLUMNS.map((column, position) => (
                        <th
                          key={column || position}
                          className={`px-2 py-1.5 text-[10px] font-black uppercase tracking-wide text-slate-400 ${
                            position === 0 ? "print:hidden" : ""
                          }`}
                        >
                          {column}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {bucket.rows.map((row) => {
                      const conflicts = conflictsByKey.get(row.draftKey) || [];
                      return (
                        <MatchRow
                          key={row.key}
                          row={row}
                          layout="venue"
                          selected={selection.has(row.iddraftmatch)}
                          onToggle={toggleSelection}
                          conflicts={conflicts}
                          severity={conflictSeverity(conflicts)}
                          entry={scheduleDraft[row.iddraftmatch]}
                          courtsByVenue={courtsByVenue}
                          onScheduleChange={onScheduleChange}
                          disabled={Boolean(busy)}
                        />
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ))}
          </section>
        );
      })}
    </div>
  );
};

export default VenueView;
